import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { NOTIFICATION_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${NOTIFICATION_SERVICE_BASE_URL}/api/v1/actionables`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 20;

export default function useActionables() {
  const authToken = sessionStorage.getItem("authToken");

  const [actionables, setActionables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  /* ------------------ Error Handler ------------------ */
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      const status = err.response.status;
      if (status >= 500 && status < 600) {
        message = "Server error. Please try again later.";
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message = "No response from server. Check your connection.";
    } else {
      message = err.message || message;
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  /* ------------------ Helper to Normalize Data ------------------ */
  const formatActionableData = (data) => ({
    id: data.id,
    message: data.message,
    userId: data.userId,
    actionItemType: data.actionItemType,
    actionItemId: data.actionItemId,
    displayType: data.displayType,
    urgency: data.urgency,
    status: data.status,
    screen: data.screen,
    deepLink: data.deepLink,
    source: data.source,
    destination: data.destination,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  });

  /* ------------------ Fetch Actionables for Specific User ------------------ */
  const fetchUserActionables = useCallback(
    async (userId, page = 0, size = 20) => {
      if (!authToken || !userId) return;
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });
        const res = await apiClient.get(`${BASE_URL}/user/${userId}`, {
          params,
        });
        const { content = [], totalPages = 1, totalElements = 0 } = res.data;

        setActionables(content.map(formatActionableData));
        setTotalPages(totalPages);
        setTotalElements(totalElements);
      } catch (err) {
        handleAxiosErrors(err, "fetch user actionables");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  /* ------------------ Search & Filter Actionables ------------------ */
  const searchActionables = useCallback(
    async ({
      search = "",
      status = "ALL",
      displayType = "ALL",
      urgency = "ALL",
      page = DEFAULT_PAGE,
      size = DEFAULT_SIZE,
    } = {}) => {
      if (!authToken) return;
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        if (status && status !== "ALL") params.append("status", status);
        if (displayType && displayType !== "ALL")
          params.append("displayType", displayType);
        if (urgency && urgency !== "ALL") params.append("urgency", urgency);

        if (search?.trim().length >= 3) {
          params.append("q", search.trim());
        }

        const endpoint = params.toString()
          ? `${BASE_URL}/search?${params.toString()}`
          : BASE_URL;
        const res = await apiClient.get(endpoint);
        const { content = [], totalPages = 1, totalElements = 0 } = res.data;

        setActionables(content.map(formatActionableData));
        setTotalPages(totalPages);
        setTotalElements(totalElements);
      } catch (err) {
        handleAxiosErrors(err, "search actionables");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  /* ------------------ CRUD Operations ------------------ */
  const fetchActionableById = useCallback(
    async (actionableId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.get(`${BASE_URL}/${actionableId}`);
        return formatActionableData(res.data);
      } catch (err) {
        handleAxiosErrors(err, "fetch actionable by id");
      }
    },
    [authToken],
  );

  const createActionable = useCallback(
    async (payload) => {
      if (!authToken) return;

      const tempId = `temp-${Date.now()}`;
      const optimisticItem = {
        id: tempId,
        ...payload,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: payload.status || "PENDING",
      };

      setActionables((prev) => [optimisticItem, ...prev]);

      try {
        const res = await apiClient.post(BASE_URL, payload);
        const realItem = formatActionableData(res.data);

        setActionables((prev) =>
          prev.map((item) => (item.id === tempId ? realItem : item)),
        );

        toast.success("Actionable created successfully");
        return realItem;
      } catch (err) {
        setActionables((prev) => prev.filter((item) => item.id !== tempId));
        handleAxiosErrors(err, "create actionable");
      }
    },
    [authToken],
  );

  const updateActionable = useCallback(
    async (actionableId, payload) => {
      if (!authToken) return;

      const previousActionables = [...actionables];

      setActionables((prev) =>
        prev.map((item) =>
          item.id === actionableId
            ? { ...item, ...payload, updatedAt: new Date().toISOString() }
            : item,
        ),
      );

      try {
        const res = await apiClient.patch(
          `${BASE_URL}/${actionableId}`,
          payload,
        );
        const updatedItem = formatActionableData(res.data);

        setActionables((prev) =>
          prev.map((item) => (item.id === actionableId ? updatedItem : item)),
        );

        toast.success("Actionable updated successfully");
        return updatedItem;
      } catch (err) {
        setActionables(previousActionables);
        handleAxiosErrors(err, "update actionable");
      }
    },
    [authToken, actionables],
  );

  return {
    actionables,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchUserActionables,
    searchActionables,
    fetchActionableById,
    createActionable,
    updateActionable,
  };
}
